/**
 * Single index-conversion boundary for pipeline UI geometry.
 */

import { PIPELINE_STAGE } from '../../../constants/pipelineStages';
import {
  CAPTURE_PROFILE,
  SOURCE_FORMAT,
  normalizeCaptureProfile,
  normalizeSourceFormat,
} from '../../../constants/captureSchema';
import { VIEWER_PHASE_TITLES } from '../../../utils/viewerLoadingCopy';

export const PIPELINE_PHASE = {
  CLIENT_UPLOAD: 'clientUpload',
  QUEUED: 'queued',
  STITCHING_360: 'stitching360',
  ANALYZING_FRAMES: 'analyzingFrames',
  BUILDING_STRUCTURE: 'buildingStructure',
  RENDERING: 'rendering',
  SOG_CONVERSION: 'sogConversion',
  VIEWER_PREP: 'viewerPrep',
  VIEWER_LOAD: 'viewerLoad',
  READY: 'ready',
  FAILED: 'failed',
  CANCELLED: 'cancelled',
  CHECKING: 'checking',
};

export const LIVE_STATUS_LABELS = {
  [PIPELINE_PHASE.CLIENT_UPLOAD]: 'UPLOADING VIDEO',
  [PIPELINE_PHASE.QUEUED]: 'QUEUEING',
  [PIPELINE_PHASE.STITCHING_360]: 'STITCHING 360 VIDEO',
  [PIPELINE_PHASE.ANALYZING_FRAMES]: 'IMAGE EXTRACTION',
  [PIPELINE_PHASE.BUILDING_STRUCTURE]: 'ANALYZING IMAGES',
  [PIPELINE_PHASE.RENDERING]: 'CREATING 3D MODEL',
  [PIPELINE_PHASE.SOG_CONVERSION]: 'SOG CONVERSION',
  [PIPELINE_PHASE.VIEWER_PREP]: VIEWER_PHASE_TITLES.VIEWER_PREP,
  [PIPELINE_PHASE.VIEWER_LOAD]: VIEWER_PHASE_TITLES.VIEWER_LOAD,
  [PIPELINE_PHASE.READY]: 'RECONSTRUCTION COMPLETE',
  [PIPELINE_PHASE.FAILED]: 'FAILED',
  [PIPELINE_PHASE.CANCELLED]: 'CANCELLED',
  [PIPELINE_PHASE.CHECKING]: 'CHECKING',
};

/** Map canonical Firestore stage to pipeline phase (no percent inference). */
export function phaseFromCanonicalStage(stage) {
  switch (stage) {
  case PIPELINE_STAGE.UPLOADING:
    return PIPELINE_PHASE.CLIENT_UPLOAD;
  case PIPELINE_STAGE.STITCHING_360:
    return PIPELINE_PHASE.STITCHING_360;
  case PIPELINE_STAGE.ANALYZING_FRAMES:
    return PIPELINE_PHASE.ANALYZING_FRAMES;
  case PIPELINE_STAGE.BUILDING_STRUCTURE:
    return PIPELINE_PHASE.BUILDING_STRUCTURE;
  case PIPELINE_STAGE.RENDERING:
    return PIPELINE_PHASE.RENDERING;
  case PIPELINE_STAGE.COMPLETE:
  case PIPELINE_STAGE.DONE:
    return PIPELINE_PHASE.READY;
  default:
    return null;
  }
}

export function segmentIndexFromPhase(phase) {
  switch (phase) {
  case PIPELINE_PHASE.CLIENT_UPLOAD:
  case PIPELINE_PHASE.QUEUED:
    return 0;
  case PIPELINE_PHASE.STITCHING_360:
  case PIPELINE_PHASE.ANALYZING_FRAMES:
    return 1;
  case PIPELINE_PHASE.BUILDING_STRUCTURE:
    return 2;
  case PIPELINE_PHASE.RENDERING:
  case PIPELINE_PHASE.SOG_CONVERSION:
  case PIPELINE_PHASE.VIEWER_PREP:
    return 3;
  case PIPELINE_PHASE.VIEWER_LOAD:
  case PIPELINE_PHASE.READY:
    return 3;
  default:
    return 0;
  }
}

export function hudNotchFromPhase(phase) {
  switch (phase) {
  case PIPELINE_PHASE.CLIENT_UPLOAD:
    return 0;
  case PIPELINE_PHASE.QUEUED:
    return -1;
  case PIPELINE_PHASE.STITCHING_360:
  case PIPELINE_PHASE.ANALYZING_FRAMES:
    return 1;
  case PIPELINE_PHASE.BUILDING_STRUCTURE:
    return 2;
  case PIPELINE_PHASE.RENDERING:
    return 3;
  case PIPELINE_PHASE.SOG_CONVERSION:
  case PIPELINE_PHASE.VIEWER_PREP:
    return 4;
  case PIPELINE_PHASE.VIEWER_LOAD:
    return 5;
  case PIPELINE_PHASE.READY:
    return 5;
  default:
    return 0;
  }
}

/** Firestore stores 1-based failedStageIndex (1=upload … 5=SOG). UI segments are 0–3. */
export function fromFirestoreFailedStageIndex(idx) {
  if (!Number.isFinite(idx)) return -1;
  if (idx >= 1 && idx <= 5) {
    if (idx === 1) return 0;
    if (idx === 2) return 1;
    if (idx === 3) return 2;
    return 3;
  }
  if (idx >= 0 && idx <= 3) return idx;
  return -1;
}

/** SOG conversion and viewer prep are badge/ARC surfaces only — not stage-bar sublabels. */
export function barSublabelFromPhase(_phase) {
  return null;
}

/** Shared upload label for badge, ARC, and capture card (bar segment stays VIDEO UPLOAD). */
export function resolveUploadStatusLabel({ captureProfile = null, sourceFormat = null } = {}) {
  if (normalizeSourceFormat(sourceFormat) === SOURCE_FORMAT.INSV) return 'UPLOADING INSV';
  if (normalizeCaptureProfile(captureProfile) === CAPTURE_PROFILE.THREE_SIXTY) {
    return 'UPLOADING 360 VIDEO';
  }
  return LIVE_STATUS_LABELS[PIPELINE_PHASE.CLIENT_UPLOAD];
}

/** Map HUD notch (0–5) to LoadingUI internal stage scale (1–6, -1 queue). */
export function internalStageFromHudNotch(hudNotch) {
  if (hudNotch == null) return 0;
  if (hudNotch < 0) return -1;
  if (hudNotch >= 5) return 6;
  return hudNotch + 1;
}

export function resolveViewerPrepHintLines(viewerPrepMs) {
  const ms = Number.isFinite(viewerPrepMs) ? viewerPrepMs : 0;
  const safe = 'Safe to close this tab or come back later.';
  if (ms < 2 * 60 * 1000) {
    return [
      'Compressing the model so it opens quickly in your browser.',
      safe,
    ];
  }
  if (ms < 6 * 60 * 1000) {
    return [
      'Still compressing — larger captures can take a few minutes.',
      safe,
    ];
  }
  return [
    'This capture is unusually large — we are still working on it.',
    safe,
  ];
}
